import { useState, useEffect } from 'react';
import { reportsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, Play, Clock, Mail, Loader2, Calendar, CalendarDays } from 'lucide-react';
import AppShell from '../components/AppShell';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function ScheduledReportsPage() {
  const [schedules, setSchedules] = useState([]);
  const [savedReports, setSavedReports] = useState([]);
  const [emailLog, setEmailLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [running, setRunning] = useState(null);
  // Create form
  const [formReportId, setFormReportId] = useState('');
  const [formFreq, setFormFreq] = useState('daily');
  const [formDow, setFormDow] = useState('0');
  const [formDom, setFormDom] = useState('1');
  const [formHour, setFormHour] = useState('8');
  const [formRecipients, setFormRecipients] = useState('');
  const [formFormat, setFormFormat] = useState('csv');

  useEffect(() => {
    const load = async () => {
      try {
        const [sRes, rRes, eRes] = await Promise.all([
          reportsAPI.listSchedules(),
          reportsAPI.listSaved(),
          reportsAPI.getEmailLog(),
        ]);
        setSchedules(sRes.data || []);
        setSavedReports(rRes.data || []);
        setEmailLog(eRes.data || []);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  const handleCreate = async () => {
    if (!formReportId) return;
    try {
      const res = await reportsAPI.createSchedule({
        report_id: formReportId,
        frequency: formFreq,
        day_of_week: formFreq === 'weekly' ? parseInt(formDow) : undefined,
        day_of_month: formFreq === 'monthly' ? parseInt(formDom) : undefined,
        hour: parseInt(formHour),
        recipients: formRecipients.split(',').map(s => s.trim()).filter(Boolean),
        format: formFormat,
      });
      setSchedules(prev => [res.data, ...prev]);
      setShowCreate(false);
      setFormReportId('');
      toast.success('Schedule created');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create schedule'); }
  };

  const handleToggle = async (id, enabled) => {
    try {
      const res = await reportsAPI.updateSchedule(id, { enabled });
      setSchedules(prev => prev.map(s => s.id === id ? res.data : s));
    } catch { toast.error('Update failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await reportsAPI.deleteSchedule(id);
      setSchedules(prev => prev.filter(s => s.id !== id));
      toast.success('Schedule deleted');
    } catch { toast.error('Delete failed'); }
  };

  const handleRunNow = async (id) => {
    setRunning(id);
    try {
      const res = await reportsAPI.runScheduleNow(id);
      toast.success(`Report sent to ${res.data?.email_log?.recipients?.join(', ') || 'recipients'} (${res.data?.email_log?.row_count} rows)`);
      // Refresh
      const [sRes, eRes] = await Promise.all([reportsAPI.listSchedules(), reportsAPI.getEmailLog()]);
      setSchedules(sRes.data || []);
      setEmailLog(eRes.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Run failed'); }
    setRunning(null);
  };

  if (loading) {
    return <AppShell><div className="flex items-center justify-center h-[calc(100vh-4rem)]"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--brand))]" /></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="scheduled-reports-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold" style={{ fontFamily: 'DM Sans' }}>Scheduled Reports</h1>
            <p className="text-sm text-muted-foreground mt-1">Automatically send saved reports on a recurring schedule</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="create-schedule-btn">
            <Plus className="w-4 h-4 mr-1" />New Schedule
          </Button>
        </div>

        {/* Schedules Table */}
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Report</TableHead>
              <TableHead>Frequency</TableHead>
              <TableHead>Recipients</TableHead>
              <TableHead>Next Run</TableHead>
              <TableHead>Last Run</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {schedules.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No scheduled reports yet</TableCell></TableRow>
              ) : schedules.map(s => (
                <TableRow key={s.id} data-testid={`schedule-row-${s.id}`}>
                  <TableCell>
                    <p className="font-medium text-sm">{s.report_name}</p>
                    <Badge variant="outline" className="text-[10px] mt-0.5">{s.format?.toUpperCase()}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {s.frequency === 'daily' && <Calendar className="w-3.5 h-3.5 text-muted-foreground" />}
                      {s.frequency === 'weekly' && <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />}
                      {s.frequency === 'monthly' && <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />}
                      <span className="text-sm capitalize">{s.frequency}</span>
                      {s.frequency === 'weekly' && <span className="text-xs text-muted-foreground">({DAYS[s.day_of_week || 0]})</span>}
                      {s.frequency === 'monthly' && <span className="text-xs text-muted-foreground">(Day {s.day_of_month || 1})</span>}
                      <span className="text-xs text-muted-foreground">at {s.hour || 8}:00 UTC</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs max-w-[160px] truncate">{(s.recipients || []).join(', ') || '—'}</TableCell>
                  <TableCell className="text-xs font-mono">{s.next_run_at ? new Date(s.next_run_at).toLocaleString() : '—'}</TableCell>
                  <TableCell>
                    {s.last_run_at ? (
                      <div>
                        <span className="text-xs font-mono">{new Date(s.last_run_at).toLocaleString()}</span>
                        <Badge className={`ml-1 text-[9px] ${s.last_status === 'success' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>{s.last_status}</Badge>
                      </div>
                    ) : <span className="text-xs text-muted-foreground">Never</span>}
                  </TableCell>
                  <TableCell>
                    <Switch checked={s.enabled} onCheckedChange={v => handleToggle(s.id, v)} data-testid={`toggle-${s.id}`} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleRunNow(s.id)} disabled={running === s.id} data-testid={`run-now-${s.id}`} title="Run now">
                        {running === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(s.id)} data-testid={`delete-schedule-${s.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Email Log */}
        <div>
          <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'DM Sans' }}>Email Log</h2>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Sent At</TableHead>
                <TableHead>Report</TableHead>
                <TableHead>Recipients</TableHead>
                <TableHead>Format</TableHead>
                <TableHead>Rows</TableHead>
                <TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {emailLog.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No emails sent yet. Run a schedule or click "Run Now".</TableCell></TableRow>
                ) : emailLog.map(e => (
                  <TableRow key={e.id} data-testid={`email-log-${e.id}`}>
                    <TableCell className="text-xs font-mono">{new Date(e.sent_at).toLocaleString()}</TableCell>
                    <TableCell className="text-sm">{e.report_name}</TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate">{(e.recipients || []).join(', ')}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{e.format?.toUpperCase()}</Badge></TableCell>
                    <TableCell className="text-sm tabular-nums">{e.row_count}</TableCell>
                    <TableCell><Badge className="text-[10px] bg-green-500/10 text-green-500">{e.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            <Mail className="w-3 h-3 inline mr-1" />
            Email delivery is currently in preview mode. Reports are generated and logged but not yet emailed. Plug in SendGrid or Resend to enable delivery.
          </p>
        </div>

        {/* Create Schedule Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-md" data-testid="create-schedule-dialog">
            <DialogHeader><DialogTitle>New Scheduled Report</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="mb-1.5 block text-xs">Saved Report</Label>
                <Select value={formReportId} onValueChange={setFormReportId}>
                  <SelectTrigger data-testid="sched-report-select"><SelectValue placeholder="Select report" /></SelectTrigger>
                  <SelectContent>
                    {savedReports.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block text-xs">Frequency</Label>
                  <Select value={formFreq} onValueChange={setFormFreq}>
                    <SelectTrigger data-testid="sched-freq-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formFreq === 'weekly' && (
                  <div>
                    <Label className="mb-1.5 block text-xs">Day of Week</Label>
                    <Select value={formDow} onValueChange={setFormDow}>
                      <SelectTrigger data-testid="sched-dow-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {formFreq === 'monthly' && (
                  <div>
                    <Label className="mb-1.5 block text-xs">Day of Month</Label>
                    <Input type="number" min={1} max={28} value={formDom} onChange={e => setFormDom(e.target.value)} data-testid="sched-dom-input" />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block text-xs">Hour (UTC)</Label>
                  <Input type="number" min={0} max={23} value={formHour} onChange={e => setFormHour(e.target.value)} data-testid="sched-hour-input" />
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs">Format</Label>
                  <Select value={formFormat} onValueChange={setFormFormat}>
                    <SelectTrigger data-testid="sched-format-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="csv">CSV</SelectItem>
                      <SelectItem value="excel">Excel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block text-xs">Recipients (comma-separated emails)</Label>
                <Input value={formRecipients} onChange={e => setFormRecipients(e.target.value)} placeholder="user@example.com, team@example.com" data-testid="sched-recipients-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={!formReportId} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="confirm-create-schedule-btn">
                <Clock className="w-4 h-4 mr-1" />Create Schedule
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
